/**
 * Office rig: single hard key spotlight carrying the venetian-blind gobo
 * (canvas stripe texture projected via SpotLight.map, shadows on), a warm
 * downward desk spot pooled under WORLD's lamp shade, and a faint additive
 * striped shaft so the light through the blinds reads in the air.
 * Owned by LIGHT; see CONTRACTS.md.
 */
import {
  AdditiveBlending,
  CanvasTexture,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  PointLight,
  Scene,
  SpotLight
} from 'three'
import { lerp } from './blend'
import {
  DESK_LAMP_ANGLE,
  DESK_LAMP_COLOR,
  DESK_LAMP_DECAY,
  DESK_LAMP_DISTANCE,
  DESK_LAMP_INTENSITY_INDOOR,
  DESK_LAMP_INTENSITY_OUTDOOR,
  DESK_LAMP_PENUMBRA,
  DESK_LAMP_POSITION,
  DESK_LAMP_TARGET,
  KEY_ANGLE,
  KEY_COLOR,
  KEY_DECAY,
  KEY_DISTANCE,
  KEY_INTENSITY_INDOOR,
  KEY_INTENSITY_OUTDOOR,
  KEY_PENUMBRA,
  KEY_POSITION,
  KEY_SHADOW_BIAS,
  KEY_SHADOW_MAP_SIZE,
  KEY_SHADOW_NEAR,
  KEY_TARGET,
  OFFICE_FILL_COLOR,
  OFFICE_FILL_DECAY,
  OFFICE_FILL_DISTANCE,
  OFFICE_FILL_INTENSITY_INDOOR,
  OFFICE_FILL_INTENSITY_OUTDOOR,
  OFFICE_FILL_POSITION,
  SHAFT_CENTER,
  SHAFT_LENGTH,
  SHAFT_OPACITY,
  SHAFT_STRIPE_COUNT,
  SHAFT_TILT_RAD,
  SHAFT_WIDTH
} from './constants'
import { createVenetianGoboTexture } from './gobo'

export interface OfficeRig {
  /** weight: 1 = outdoor, 0 = indoor. dim: finale multiplier. */
  applyBlend(weight: number, dim: number): void
}

const SHAFT_TEXTURE_SIZE_PX = 128
const SHAFT_OUTDOOR_SCALE = 0.3

/**
 * Stripes along the shaft (matching the slat gaps) that dissolve with
 * distance from the window — so the blades of light hang in the air.
 */
const createShaftTexture = (): CanvasTexture => {
  const canvas = document.createElement('canvas')
  canvas.width = SHAFT_TEXTURE_SIZE_PX
  canvas.height = SHAFT_TEXTURE_SIZE_PX
  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('GUMSHOE lighting: 2D canvas context unavailable for shaft')
  }
  context.fillStyle = '#000000'
  context.fillRect(0, 0, SHAFT_TEXTURE_SIZE_PX, SHAFT_TEXTURE_SIZE_PX)
  const period = SHAFT_TEXTURE_SIZE_PX / SHAFT_STRIPE_COUNT
  // Stripes run along x (shaft length); fade left (room) -> right (window).
  const fade = context.createLinearGradient(0, 0, SHAFT_TEXTURE_SIZE_PX, 0)
  fade.addColorStop(0, 'rgba(255, 255, 255, 0)')
  fade.addColorStop(0.55, 'rgba(255, 255, 255, 0.65)')
  fade.addColorStop(1, 'rgba(255, 255, 255, 1)')
  context.fillStyle = fade
  for (let stripe = 0; stripe < SHAFT_STRIPE_COUNT; stripe += 1) {
    const top = stripe * period + period * 0.3
    context.fillRect(0, top, SHAFT_TEXTURE_SIZE_PX, period * 0.4)
  }
  return new CanvasTexture(canvas)
}

const createShaft = (scene: Scene): MeshBasicMaterial => {
  const material = new MeshBasicMaterial({
    map: createShaftTexture(),
    color: KEY_COLOR,
    transparent: true,
    opacity: SHAFT_OPACITY,
    blending: AdditiveBlending,
    depthWrite: false,
    side: DoubleSide
  })
  const shaft = new Mesh(new PlaneGeometry(SHAFT_LENGTH, SHAFT_WIDTH), material)
  // Lay the plane flat (length along x, width along z), then tilt it down
  // along the key light's rake; the +x (window) end rides high.
  shaft.rotation.order = 'ZXY'
  shaft.rotation.x = -Math.PI / 2
  shaft.rotation.z = SHAFT_TILT_RAD
  shaft.position.set(SHAFT_CENTER.x, SHAFT_CENTER.y, SHAFT_CENTER.z)
  scene.add(shaft)
  return material
}

export const createOfficeRig = (scene: Scene): OfficeRig => {
  const key = new SpotLight(
    KEY_COLOR,
    KEY_INTENSITY_INDOOR,
    KEY_DISTANCE,
    KEY_ANGLE,
    KEY_PENUMBRA,
    KEY_DECAY
  )
  key.position.set(KEY_POSITION.x, KEY_POSITION.y, KEY_POSITION.z)
  key.target.position.set(KEY_TARGET.x, KEY_TARGET.y, KEY_TARGET.z)
  // SpotLight.map projection requires castShadow — we want shadows here anyway.
  key.castShadow = true
  key.map = createVenetianGoboTexture()
  key.shadow.mapSize.set(KEY_SHADOW_MAP_SIZE, KEY_SHADOW_MAP_SIZE)
  key.shadow.bias = KEY_SHADOW_BIAS
  key.shadow.camera.near = KEY_SHADOW_NEAR
  key.shadow.camera.far = KEY_DISTANCE
  scene.add(key)
  scene.add(key.target)

  const deskLamp = new SpotLight(
    DESK_LAMP_COLOR,
    DESK_LAMP_INTENSITY_INDOOR,
    DESK_LAMP_DISTANCE,
    DESK_LAMP_ANGLE,
    DESK_LAMP_PENUMBRA,
    DESK_LAMP_DECAY
  )
  deskLamp.position.set(DESK_LAMP_POSITION.x, DESK_LAMP_POSITION.y, DESK_LAMP_POSITION.z)
  deskLamp.target.position.set(DESK_LAMP_TARGET.x, DESK_LAMP_TARGET.y, DESK_LAMP_TARGET.z)
  deskLamp.castShadow = false
  scene.add(deskLamp)
  scene.add(deskLamp.target)

  // Low warm fill above the desk — the room reads as surfaces, not voids.
  const fill = new PointLight(
    OFFICE_FILL_COLOR,
    OFFICE_FILL_INTENSITY_INDOOR,
    OFFICE_FILL_DISTANCE,
    OFFICE_FILL_DECAY
  )
  fill.position.set(OFFICE_FILL_POSITION.x, OFFICE_FILL_POSITION.y, OFFICE_FILL_POSITION.z)
  fill.castShadow = false
  scene.add(fill)

  const shaftMaterial = createShaft(scene)

  const applyBlend = (weight: number, dim: number): void => {
    key.intensity = lerp(KEY_INTENSITY_INDOOR, KEY_INTENSITY_OUTDOOR, weight) * dim
    deskLamp.intensity =
      lerp(DESK_LAMP_INTENSITY_INDOOR, DESK_LAMP_INTENSITY_OUTDOOR, weight) * dim
    fill.intensity =
      lerp(OFFICE_FILL_INTENSITY_INDOOR, OFFICE_FILL_INTENSITY_OUTDOOR, weight) * dim
    // The shaft only reads indoors; fade it down out on the street.
    shaftMaterial.opacity = lerp(SHAFT_OPACITY, SHAFT_OPACITY * SHAFT_OUTDOOR_SCALE, weight) * dim
  }

  return { applyBlend }
}
